import ConfirmationDialogAdvanced from 'components/shared/ConfirmationDialogAdvanced';
import { t } from 'i18next';
import useGetFlexDisconnectReasons from '../hooks/useGetFlexDisconnectReasons';
import useGetFlexDutyHourTypes from '../hooks/useGetFlexDutyHourTypes';
import { useForm } from 'react-hook-form';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import { useState } from 'react';
import { deleteFlexDutyLine } from 'RestAPIs/flex-duty-line';
import { useQueryClient } from 'react-query';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const schema = yup.object({
  disconnectReason: yup
    .object()
    .nullable()
    .required(t('validation.valueIsRequired'))
});

type DisconnectReasonTypes = {
  disconnectReason: { code: string } | null;
  dutyHourType: { id: number } | null;
};

const DisconnectConfirmation = ({
  rowIdToDisconnect,
  setShowDisconnectConfirmation
}) => {
  const { control, watch, handleSubmit } = useForm<DisconnectReasonTypes>({
    resolver: yupResolver(schema)
  });
  const { data: flexDisconnectReason } = useGetFlexDisconnectReasons();
  const { data: dutyHourTypes } = useGetFlexDutyHourTypes();

  const filteredDutyHourItems = dutyHourTypes?.filter(
    (item) =>
      item.flags.includes('REQ_DISCONNECT_AUTO') &&
      item.flags.includes('SHORTLIST')
  );

  const [changeDuty, setChangeDuty] = useState(false);

  const { disconnectReason, dutyHourType } = watch();

  const queryClient = useQueryClient();

  const handleDisconnect = async (returnToFlex: boolean | null) => {
    const selectedDisConnectReason = flexDisconnectReason?.find(
      (el) => el.code === disconnectReason?.code
    );

    const selectedDutyHourType = filteredDutyHourItems?.find(
      (el) => el.id === dutyHourType?.id
    );

    const disconnectFlexReason = selectedDisConnectReason?.code;
    const flexDutyHourType = selectedDutyHourType?.id;

    try {
      await deleteFlexDutyLine(
        rowIdToDisconnect,
        disconnectFlexReason,
        flexDutyHourType,
        !!returnToFlex
      );
      queryClient.invalidateQueries({
        queryKey: ['flexHistory', 'list']
      });
      showSuccessSnackbar();
    } catch (error) {
      showErrorSnackbar(t('flexHistory.errorDisconnecting'));
    }
  };

  const onDisconnectToRoster = handleSubmit(async () => {
    await handleDisconnect(false);
    setShowDisconnectConfirmation(false);
  });

  const onDisconnectToFlex = handleSubmit(async () => {
    await handleDisconnect(true);
    setShowDisconnectConfirmation(false);
  });

  const onClose = () => {
    setShowDisconnectConfirmation(false);
  };

  const processedDisconnectReasons = (flexDisconnectReason ?? []).map(
    (option) => ({
      ...option,
      name: option.name || `${t('flexHistory.noName')} - ${option.code}` // Ensure 'name' is always defined
    })
  );

  return (
    <ConfirmationDialogAdvanced
      handleAction={() => onDisconnectToFlex()}
      handleSecondAction={() => onDisconnectToRoster()}
      onClose={onClose}
      enableSecondAction={true}
      cancelBtnText={t('cancel')}
      confirmBtnText={t('flexHistory.requestBackToFlex')}
      confirmSecondBtnText={t('flexHistory.requestBackToRoster')}
      type="info"
      title={t('flexHistory.disconnectFlexDuty')}
    >
      <Box display="flex" flexDirection="column">
        <Box sx={{ flexGrow: 2, my: 1 }}>
          <Box sx={{ mb: 1 }}>{t('flexHistory.disconnectReasonMandatory')}</Box>
          <ControlledAutoComplete
            disableSorting
            optionLabelProp="name"
            idProperty="id"
            variant="outlined"
            control={control}
            name="disconnectReason"
            label={t('flexHistory.selectDisconnectReason')}
            options={processedDisconnectReasons ?? []}
            fullWidth
          />
        </Box>
        <Box sx={{ flexGrow: 2, my: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={changeDuty}
                onChange={(evt, checked) => setChangeDuty(checked)}
              />
            }
            label={t('flexHistory.customizeDuty') as string}
          />
          <ControlledAutoComplete
            disableSorting
            optionLabelProp="description"
            disabled={!changeDuty}
            idProperty="id"
            variant="outlined"
            control={control}
            name="dutyHourType"
            label={t('flexHistory.selectDutyHourType')}
            options={filteredDutyHourItems ?? []}
            fullWidth
          />
        </Box>
        <Box sx={{ flexGrow: 2, my: 1 }}>
          <p>{t('flexHistory.selectionWarning')}</p>
        </Box>
      </Box>
    </ConfirmationDialogAdvanced>
  );
};

export default DisconnectConfirmation;
